import { CdkTable } from '@angular/cdk/table';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   Input,
   OnDestroy,
   OnInit,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import {
   ControlValueAccessor,
   FormControl,
   FormGroupDirective,
   NG_VALUE_ACCESSOR,
   UntypedFormArray,
   UntypedFormBuilder,
   UntypedFormGroup,
   Validators,
} from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { ExtensionPrivileges } from '../../../models/extension-privileges';
import { FaxEmailItemView } from './models/fax-emails-item-view';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: FaxEmailsComponent }],
    selector: 'ndt-fax-emails',
    styleUrls: ['./fax-emails.component.scss'],
    templateUrl: './fax-emails.component.html',
    standalone: false
})
export class FaxEmailsComponent implements OnInit, ControlValueAccessor, OnDestroy, DoCheck {
   @ViewChild('table') table: CdkTable<UntypedFormGroup>;

   @Input() extensionId: number | null;

   @Input() isOnAdd: boolean | null;

   @Input() callState: CallState;

   @Input() extensionPrivileges: ExtensionPrivileges;

   faxEmailItems: FaxEmailItemView[] = [];

   faxEmailsArray: UntypedFormArray;
   buttonMessage: string = 'Add Whitelisted Emails';

   disabled = false;

   columns: string[] = ['emailAddress', 'isDomainOnly'];

   form: UntypedFormGroup;

   form$: BehaviorSubject<UntypedFormGroup>;

   private onChange: (value: FaxEmailItemView[] | null) => void = () => {};

   private destroyed$ = new Subject<void>();

   onTouched: () => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private confirmationService: ConfirmationDialogService,
      private fb: UntypedFormBuilder,
      private parentForm: FormGroupDirective
   ) {
      this.faxEmailsArray = this.fb.array([]);
      this.form$ = new BehaviorSubject<UntypedFormGroup>(
         new UntypedFormGroup({
            faxEmailsArray: this.faxEmailsArray,
         })
      );
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   ngOnInit(): void {
      this.initializeForm();
      this.subscribeToFormChanges();

      if (!this.extensionPrivileges.canEditFaxEmails) {
         this.disabled = true;
      }
   }

   get hasVisibleFaxEmails(): boolean {
      return this.faxEmailsArray.controls.some((control) => !control.value.isDeleted);
   }

   private initializeForm() {
      if (this.faxEmailItems.length !== 0) {
         this.faxEmailItems.forEach((emailItem) => {
            const formGroup = this.createEmailFormGroup(emailItem);
            this.faxEmailsArray.push(formGroup);
         });
      }

      this.form = new UntypedFormGroup({
         faxEmailsArray: this.faxEmailsArray,
      });
   }

   private subscribeToFormChanges() {
      this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((formValue) => {
         this.handleFormChanges(formValue);
      });
   }

   registerOnChange(onChange: (value: FaxEmailItemView[] | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && this.parentForm.form.get(['faxEmailAddresses'])?.touched) {
         this.faxEmailsArray.markAllAsTouched();
         this.parentForm.form.markAsUntouched();
         this.changeDetectorRef.markForCheck();
      }
   }

   private handleFormChanges(formValue: any) {
      const activeControls = this.faxEmailsArray.controls.filter((control) => !control.get('isDeleted')?.value);

      const hasInvalid = activeControls.some((control) => control.invalid);

      this.onChange(formValue.faxEmailsArray as FaxEmailItemView[]);

      if (hasInvalid) {
         this.parentForm.form.setErrors({ invalidEmail: true });
      } else {
         if (this.parentForm.form.hasError('invalidEmail')) {
            this.parentForm.form.setErrors(null);
         }
      }

      this.faxEmailItems = formValue.faxEmailsArray as FaxEmailItemView[];
      this.changeDetectorRef.markForCheck();
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   writeValue(faxEmailItems: FaxEmailItemView[]): void {
      if (faxEmailItems) {
         this.faxEmailItems = faxEmailItems;
         this.faxEmailsArray.clear();
         faxEmailItems.forEach((emailItem) => {
            this.faxEmailsArray.push(this.createEmailFormGroup(emailItem));
         });
         this.changeDetectorRef.markForCheck();
         if (this.table) {
            this.table.renderRows();
         }
      }
   }

   createEmailFormGroup(emailItem?: FaxEmailItemView): UntypedFormGroup {
      return this.fb.group({
         emailAddress: [emailItem?.emailAddress, [Validators.required, this.validateInput]],
         extensionId: [this.extensionId ?? 0],
         faxEmailAddressId: [emailItem?.faxEmailAddressId],
         isDeleted: [emailItem?.isDeleted || false],
      });
   }

   validateInput(control: FormControl) {
      const emailRegex = /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/i;
      const domainRegex = /^@[a-zA-Z0-9.-]+\.[a-z]{2,}$/i;

      const atCount = (control.value.match(/@/g) || []).length;

      if (atCount > 1) {
         return {
            validateMultipleInput: {
               multipleEmails: true,
            },
         };
      }

      return emailRegex.test(control.value) || domainRegex.test(control.value)
         ? null
         : {
              validateInput: {
                 valid: false,
              },
           };
   }

   deleteConfirmationDialog(message: string): Observable<boolean> {
      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this email?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   confirmDialog(confirmed, rowIndex) {
      if (confirmed) {
         this.faxEmailItems[rowIndex].isDeleted = true;
         this.faxEmailsArray.at(rowIndex).get('isDeleted')?.setValue(true);

         // Clear validation errors (if any) on deleted rows
         if (this.faxEmailsArray.at(rowIndex).get('emailAddress')?.errors) {
            this.faxEmailsArray.at(rowIndex).get('emailAddress')?.setErrors(null);
            this.onChange(this.faxEmailItems);
         }

         this.changeDetectorRef.markForCheck();
         if (this.table) this.table.renderRows();
         this.onTouched();
      }
   }

   onDeleteClick(rowIndex: number): void {
      if (this.faxEmailItems[rowIndex].faxEmailAddressId) {
         if (this.extensionPrivileges?.canDeleteFaxEmails && rowIndex < this.faxEmailsArray.length) {
            const message = 'Changes will not take effect until you save the form.';
            this.deleteConfirmationDialog(message)
               .pipe(takeUntil(this.destroyed$))
               .subscribe((confirmed) => this.confirmDialog(confirmed, rowIndex));
         }
      } else {
         // New email not yet in db
         const message =
            'This email was recently added and has not been saved yet. Are you sure you want to delete it?';
         this.deleteConfirmationDialog(message)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((confirmed) => this.confirmDialog(confirmed, rowIndex));
      }
   }

   onAddFaxEmail(): void {
      const newEmail: FaxEmailItemView = {
         emailAddress: '',
         extensionId: this.extensionId ?? 0,
         faxEmailAddressId: null,
         isDeleted: false,
      };
      this.faxEmailItems.push(newEmail);
      const newFormGroup = this.createEmailFormGroup(newEmail);
      this.faxEmailsArray.push(newFormGroup);

      this.changeDetectorRef.markForCheck();
      if (this.table) this.table.renderRows();
   }

   isDomainOnly(index: number): boolean {
      const emailControl = this.faxEmailsArray.at(index).get('emailAddress') as FormControl;
      const domainPattern = /^@[a-zA-Z0-9.-]+\.[a-z]{2,}$/i;
      return domainPattern.test(emailControl.value);
   }
}
